import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Calendar, MapPin, Users, Gavel, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";
const TICKET_IMAGE = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/uuqjw5c4_Ticket3.png";

const AuctionCard = ({ event, hasTicket = false, onGetTicket, t }) => {
    const navigate = useNavigate();
    const startDate = new Date(event.start_datetime);
    const isLive = event.status === 'live';
    const isArchived = event.status === 'archived';

    // Use ticket image for upcoming events
    const coverImage = isArchived 
        ? (event.cover_images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop')
        : TICKET_IMAGE;

    return (
        <div
            className="group bg-white border border-border/30 overflow-hidden transition-all duration-300 hover:shadow-lg"
            data-testid={`auction-card-${event.id}`}
        >
            {/* Portrait aspect ratio for full image visibility */}
            <div className="relative aspect-[3/4] overflow-hidden">
                <img
                    src={coverImage}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                
                {isLive && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-[#F8BBD6] text-foreground text-xs font-mono uppercase">
                        <span className="w-2 h-2 rounded-full bg-foreground animate-pulse" />
                        {t('liveNow')}
                    </div>
                )}
                
                {isArchived && (
                    <div className="absolute top-3 left-3 px-3 py-1.5 bg-white/90 text-muted-foreground text-xs font-mono uppercase">
                        {t('archived')}
                    </div>
                )}

                {!isArchived && (
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-white/90 text-foreground text-xs font-mono uppercase">
                        Spring/Summer 26
                    </div>
                )}
            </div>

            <div className="p-8">
                <h3 className="font-heading text-2xl font-semibold mb-4 group-hover:text-[#F8BBD6] transition-colors">
                    {event.title}
                </h3>

                <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>{format(startDate, 'EEEE, MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location_name}</span>
                    </div>
                    {!isArchived && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>{event.capacity} {t('capacity')}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-border/30">
                    {event.ticket_price > 0 ? (
                        <span className="font-mono text-lg">€{event.ticket_price.toFixed(0)}</span>
                    ) : (
                        <span className="text-sm text-muted-foreground font-medium">By invitation</span>
                    )}

                    {!isArchived && (
                        hasTicket ? (
                            isLive ? (
                                <Button
                                    onClick={() => navigate(`/gallery?event=${event.id}`)}
                                    className="rounded-full bg-[#F8BBD6] hover:bg-[#f0a8c8] text-foreground"
                                    data-testid={`enter-auction-${event.id}`}
                                >
                                    <Gavel className="w-4 h-4 mr-2" />
                                    {t('enterAuction')}
                                </Button>
                            ) : (
                                <div className="flex items-center gap-2 text-[#F8BBD6]">
                                    <Gavel className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('accessGranted')}</span>
                                </div>
                            )
                        ) : (
                            /* Get Ticket button for users without ticket */
                            <Button
                                onClick={() => onGetTicket ? onGetTicket(event) : navigate(`/auction/${event.id}`)}
                                className="rounded-full bg-black hover:bg-black/90 text-white"
                                data-testid={`get-ticket-${event.id}`}
                            >
                                Get Ticket
                            </Button>
                        )
                    )}

                    {isArchived && (
                        <Button
                            onClick={() => navigate(`/auction/${event.id}`)}
                            variant="ghost"
                            className="text-muted-foreground"
                        >
                            View Details
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AuctionPage = () => {
    const { t } = useLanguage();
    const { api } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [events, setEvents] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upcoming');

    useEffect(() => {
        fetchData();
        
        const ticketStatus = searchParams.get('ticket');
        const sessionId = searchParams.get('session_id');
        if (ticketStatus === 'success' && sessionId) {
            pollTicketStatus(sessionId);
        }
    }, []);

    const fetchData = async () => {
        try {
            const [eventsRes, ticketsRes] = await Promise.all([
                api().get('/events/'),
                api().get('/tickets/my'),
            ]);
            setEvents(eventsRes.data);
            setTickets(ticketsRes.data);
        } catch (error) {
            console.error('Failed to fetch events:', error);
            toast.error('Failed to load auctions');
        } finally {
            setLoading(false);
        }
    };

    const pollTicketStatus = async (sessionId, attempts = 0) => {
        if (attempts >= 5) {
            toast.info('Payment status check timed out. Please refresh.');
            return;
        }

        try {
            const response = await api().get(`/tickets/status/${sessionId}`);
            if (response.data.payment_status === 'paid') {
                toast.success('Access granted!');
                fetchData();
                navigate('/auction', { replace: true });
            } else {
                setTimeout(() => pollTicketStatus(sessionId, attempts + 1), 2000);
            }
        } catch (error) {
            console.error('Status check error:', error);
        }
    };

    const handleGetTicket = async (event) => {
        try {
            const response = await api().post('/tickets/purchase', {
                event_id: event.id,
                origin_url: window.location.origin,
            });

            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else if (response.data.ticket_id) {
                toast.success('Access granted!');
                fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to request access');
        }
    };

    const hasTicket = (eventId) => {
        return tickets.some(t => t.event_id === eventId && t.payment_status === 'paid');
    };

    const liveEvents = events.filter(e => e.status === 'live');
    // Only show 1 upcoming event
    const upcomingEvents = events.filter(e => e.status === 'upcoming').slice(0, 1);
    const archivedEvents = events.filter(e => e.status === 'archived');

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" />
                </div>
            </div>

            <main className="pt-20 px-4 max-w-4xl mx-auto">
                {/* Page Title */}
                <h1 className="text-2xl font-heading font-light tracking-tight mb-8">
                    Auction Events
                </h1>

                {/* Live Auction Banner */}
                {liveEvents.length > 0 && (
                    <div className="mb-12 animate-fade-in">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="w-2 h-2 rounded-full bg-black animate-pulse" />
                            <h2 className="text-xs tracking-widest uppercase">{t('liveNow')}</h2>
                        </div>
                        {liveEvents.map(event => (
                            <AuctionCard
                                key={event.id}
                                event={event}
                                hasTicket={hasTicket(event.id)}
                                onGetTicket={handleGetTicket}
                                t={t}
                            />
                        ))}
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full bg-white p-1 mb-8 border border-gray-200">
                        <TabsTrigger
                            value="upcoming"
                            className="flex-1 text-xs tracking-widest uppercase data-[state=active]:bg-black data-[state=active]:text-white"
                            data-testid="tab-upcoming"
                        >
                            Upcoming ({upcomingEvents.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="archived"
                            className="flex-1 text-xs tracking-widest uppercase data-[state=active]:bg-black data-[state=active]:text-white"
                            data-testid="tab-archived"
                        >
                            Past ({archivedEvents.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="upcoming" className="space-y-8">
                        {loading ? (
                            <>
                                <Skeleton className="h-96 w-full bg-white" />
                                <Skeleton className="h-96 w-full bg-white" />
                            </>
                        ) : upcomingEvents.length === 0 ? (
                            <div className="text-center py-20">
                                <p className="text-muted-foreground font-accent italic text-lg">
                                    No upcoming auctions at the moment
                                </p>
                            </div>
                        ) : (
                            upcomingEvents.map((event, index) => (
                                <div
                                    key={event.id}
                                    className="animate-slide-up"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <AuctionCard
                                        event={event}
                                        hasTicket={hasTicket(event.id)}
                                        onGetTicket={handleGetTicket}
                                        t={t}
                                    />
                                </div>
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="archived" className="space-y-8">
                        {loading ? (
                            <Skeleton className="h-96 w-full bg-white" />
                        ) : archivedEvents.length === 0 ? (
                            <div className="text-center py-20">
                                <p className="text-muted-foreground font-accent italic text-lg">
                                    No archived auctions yet
                                </p>
                            </div>
                        ) : (
                            archivedEvents.map((event, index) => (
                                <div
                                    key={event.id}
                                    className="animate-slide-up"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <AuctionCard event={event} hasTicket={hasTicket(event.id)} t={t} />
                                </div>
                            ))
                        )}
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default AuctionPage;
